/**
 * UserNotesDoc + DeckDoc — per-user Yjs document, scoped per-deck via views.
 *
 * ARCHITECTURE
 * ------------
 * ONE Y.Doc per user hosts ALL the user's collaborative state — notes across
 * every deck, plus (reserved) annotations, handwriting, and embeds. The doc
 * lives in a single NotesDo room (`{userId}--notes`). This keeps the
 * WebSocket + IndexedDB handle count to ONE per session, regardless of how
 * many decks the user navigates between. Navigating dashboard → deck A →
 * deck B → deck A is effectively free after the initial connect.
 *
 * Previously this was one Y.Doc per deck. That meant a full handshake +
 * fresh IDB persistence on every deck navigation — see the commit that
 * introduced this module for the perf rationale.
 *
 * SCHEMA (top-level Y types on the user's Y.Doc)
 * ----------------------------------------------
 *
 *   Y.Doc("user:{userId}:notes")
 *     ├─ __meta: Y.Map<string, unknown>       // schemaVersion, seededFromLegacy, ...
 *     └─ decks:  Y.Map<deckId, Y.Map>         // one sub-map per deck
 *
 *   Deck sub-map (keyed under `decks[deckId]`):
 *     ├─ notes:        Y.Map<noteId, NoteItem>
 *     ├─ annotations:  Y.Map<...>                    // reserved
 *     ├─ handwriting:  Y.Map<pageNum, Y.Array<...>>  // reserved
 *     └─ embeds:       Y.Map<id, Y.Map<...>>         // reserved
 *
 *   NoteItem (a deck can have many notes, each of any kind):
 *     ├─ type: "tiptap" | "youtube" | "link" | <future kind>
 *     └─ <type-specific fields on the same Y.Map>
 *         tiptap  → { content: Y.XmlFragment, title?: string }
 *         youtube → { url, title?, thumbnail?, description? }
 *         link    → { url, title?, description?, favicon? }
 *
 * EXTENSIBILITY
 * -------------
 * Adding a new note kind (e.g. "image", "figma-embed"):
 *   1. Pick a string literal for `NoteKind`.
 *   2. Define the Y-stored fields (flat scalars, Y.Map, Y.XmlFragment — any
 *      Yjs type works).
 *   3. Add a `get<Kind>NoteMeta()` reader + `set<Kind>NoteMeta()` writer on
 *      `DeckDoc` or a subclass. NoteItem is a Y.Map — just set/get keys.
 *   4. UI branches on `deckDoc.getNoteKind(id)`.
 * No server change, no schema migration, no wire-format change. The
 * NotesDo stores whatever Yjs hands it.
 *
 * LIFECYCLE
 * ---------
 * - UserNotesDoc is created once by `<UserNotesDocProvider>` at app level
 *   (inside SyncProvider) and lives for the duration of the signed-in
 *   session.
 * - DeckDoc is a CHEAP per-deck view. A fresh DeckDoc can be constructed
 *   around the same UserNotesDoc + deckId at any time; there's no "owner".
 * - Deleting a deck removes `decks.get(deckId)` from the user doc (see
 *   scoped-deck / sync-slice wiring).
 */

import * as Y from 'yjs';

import { DEFAULT_NOTE_ID } from '../../notes/note-identity';

// ---------------------------------------------------------------------------
// Diagnostic: pre-sync auto-create detector
// ---------------------------------------------------------------------------
//
// Auto-creating Y.Map structure on an unsynced Y.Doc is a known data-loss
// race: the local empty maps can win Yjs's concurrent-write tiebreaker
// against the server's rich data and tombstone the user's notes. The
// editor mount path (`notes-view.tsx`) gates auto-create on a strict
// `notesSynced` flag — this function is a defensive secondary log that
// fires if anything in the future bypasses that gate.
//
// Heuristic for "pre-sync": the doc has fewer than 2 known clientIDs in
// its store AND no deck submaps. A freshly-loaded doc that has finished
// IDB or WebSocket sync will have ops from the original authoring
// clientIDs (typically dozens after a session).

function warnIfPreSyncAutoCreate(ydoc: Y.Doc, deckName: string, noteId: string): void {
  try {
    const clientCount = ydoc.store.clients.size;
    const decksMap = ydoc.getMap<unknown>('decks');
    const deckCount = decksMap.size;
    if (clientCount <= 1 && deckCount === 0) {
      const stateBytes = Y.encodeStateAsUpdate(ydoc).byteLength;

      console.warn(
        '[Yjs auto-create on empty doc] possible data-loss race — ' +
          'getNoteFragment fired before the doc was synced from server. ' +
          'If the user sees an empty editor after refresh, this is why.',
        {
          deckName,
          noteId,
          clientCount,
          deckCount,
          stateBytes,
          localClientID: ydoc.clientID,
        },
      );
    }
  } catch {
    // Diagnostic must never throw.
  }
}

// ---------------------------------------------------------------------------
// Schema constants
// ---------------------------------------------------------------------------

/** Top-level keys on the user Y.Doc. */
export const USER_DOC_KEYS = {
  decks: 'decks',
  meta: '__meta',
} as const;

/** Keys on a deck sub-map. */
export const DECK_SUBMAP_KEYS = {
  notes: 'notes',
  annotations: 'annotations',
  handwriting: 'handwriting',
  embeds: 'embeds',
} as const;

/** Keys on a NoteItem Y.Map. */
export const NOTE_ITEM_KEYS = {
  type: 'type',
  content: 'content', // tiptap only
  title: 'title', // tiptap, youtube, link
  url: 'url', // youtube, link
  thumbnail: 'thumbnail', // youtube
  description: 'description', // youtube, link
  favicon: 'favicon', // link
} as const;

/** Backwards-compat: the current user has exactly one note with this id
 * unless/until we ship multi-note support in the UI. The Y.Map already
 * supports arbitrary ids — this is a default, not a limit.
 *
 * DECLARED in `notes/note-identity`, which is dependency-free so the RN and
 * DOM/WebView bundles can import it without dragging Yjs along; re-exported
 * here because this module has always been where sync-client callers reach
 * for it. */
export { DEFAULT_NOTE_ID };

/** Current schema version. Stored at `__meta.schemaVersion`. Bump when the
 * on-wire shape of the user doc changes in a non-additive way. */
export const USER_DOC_SCHEMA_VERSION = 1;

/** Known note kinds. Add new literal values here to extend. */
export type NoteKind = 'tiptap' | 'youtube' | 'link';

/** Metadata stored on a youtube-kind note. */
export type YoutubeNoteMeta = {
  url: string;
  title?: string;
  thumbnail?: string;
  description?: string;
};

/** Metadata stored on a link-kind note. */
export type LinkNoteMeta = {
  url: string;
  title?: string;
  description?: string;
  favicon?: string;
};

// ---------------------------------------------------------------------------
// Back-compat aliases (old code imports these from here)
// ---------------------------------------------------------------------------
//
// `DECK_DOC_KEYS` was the per-deck-doc top-level key table in the old
// one-Y.Doc-per-deck world. External files still reference it by name, so
// keep the symbol — it now points at the deck-sub-map keys, which is the
// equivalent conceptual namespace under the new schema.

export const DECK_DOC_KEYS = DECK_SUBMAP_KEYS;

// ---------------------------------------------------------------------------
// UserNotesDoc
// ---------------------------------------------------------------------------

/**
 * Wrapper around the per-user Y.Doc.
 *
 * Owns no lifecycle — the Y.Doc + provider are managed by the connected
 * `UserNotesDocProvider`. Instances are cheap; construct one per hook
 * invocation.
 */
export class UserNotesDoc {
  readonly ydoc: Y.Doc;

  constructor(ydoc: Y.Doc) {
    this.ydoc = ydoc;
    // Stamp schema version on first touch so future code can detect old
    // docs and migrate. Idempotent — Y.Map set of equal value is a no-op.
    const meta = this.ydoc.getMap<unknown>(USER_DOC_KEYS.meta);
    if (meta.get('schemaVersion') !== USER_DOC_SCHEMA_VERSION) {
      meta.set('schemaVersion', USER_DOC_SCHEMA_VERSION);
    }
  }

  /** Access the `__meta` Y.Map. */
  meta(): Y.Map<unknown> {
    return this.ydoc.getMap<unknown>(USER_DOC_KEYS.meta);
  }

  /** Has the bulk seed from Supabase legacy notes already run? */
  get seededFromLegacy(): boolean {
    return this.meta().get('seededFromLegacy') === true;
  }

  /** Mark the legacy seed as done. Idempotent. */
  markSeededFromLegacy(): void {
    this.meta().set('seededFromLegacy', true);
  }

  /** The `decks` Y.Map — one entry per deck the user has collaborative content for. */
  decksMap(): Y.Map<Y.Map<unknown>> {
    return this.ydoc.getMap<Y.Map<unknown>>(USER_DOC_KEYS.decks);
  }

  /** Get the sub-map for a deck, creating it if missing. */
  getOrCreateDeckSubmap(deckId: string): Y.Map<unknown> {
    const decks = this.decksMap();
    let sub = decks.get(deckId);
    if (!sub) {
      sub = new Y.Map<unknown>();
      decks.set(deckId, sub);
    }
    return sub;
  }

  /** Get the sub-map for a deck or `undefined` if none exists yet. */
  getDeckSubmap(deckId: string): Y.Map<unknown> | undefined {
    return this.decksMap().get(deckId);
  }

  /** Delete a deck's entire sub-map. Called on deck-delete. */
  deleteDeckSubmap(deckId: string): void {
    const decks = this.decksMap();
    if (decks.has(deckId)) {
      decks.delete(deckId);
    }
  }

  /** Construct a DeckDoc view over a deck's sub-map. Cheap. */
  deck(deckId: string): DeckDoc {
    return new DeckDoc(deckId, this);
  }
}

// ---------------------------------------------------------------------------
// DeckDoc — per-deck view over a slice of the user doc
// ---------------------------------------------------------------------------

/**
 * Per-deck API over the user's Y.Doc. The wrapper does not own any Yjs
 * state — it dereferences into `userDoc.decksMap().get(deckId)` every time.
 *
 * The public API matches the pre-refactor per-deck DeckDoc so consumer
 * code in the editor / share flow / PDF renderer compiles unchanged.
 */
export class DeckDoc {
  readonly deckName: string;
  readonly userDoc: UserNotesDoc;

  constructor(deckName: string, userDoc: UserNotesDoc) {
    this.deckName = deckName;
    this.userDoc = userDoc;
  }

  /**
   * The user-level Y.Doc. Exposed for callers that need to batch writes
   * inside a single `ydoc.transact()` — the transaction is at the user
   * doc level, which is correct (we want to atomically apply the whole
   * change and avoid intermediate sync messages).
   *
   * Previously this was the per-deck Y.Doc; behaviour is unchanged for
   * all existing callers that just use it for `transact()`.
   */
  get ydoc(): Y.Doc {
    return this.userDoc.ydoc;
  }

  /** The deck sub-map, creating it if missing. */
  private submap(): Y.Map<unknown> {
    return this.userDoc.getOrCreateDeckSubmap(this.deckName);
  }

  /** The deck sub-map without creating — `undefined` if this deck has no content yet. */
  private submapOrUndefined(): Y.Map<unknown> | undefined {
    return this.userDoc.getDeckSubmap(this.deckName);
  }

  // ── Notes (multi-note, typed) ───────────────────────────────────────

  /** The `notes` Y.Map under this deck, creating it if missing. */
  private notesMap(): Y.Map<Y.Map<unknown>> {
    const sub = this.submap();
    let notes = sub.get(DECK_SUBMAP_KEYS.notes) as Y.Map<Y.Map<unknown>> | undefined;
    if (!notes) {
      notes = new Y.Map<Y.Map<unknown>>();
      sub.set(DECK_SUBMAP_KEYS.notes, notes);
    }
    return notes;
  }

  /** The `notes` Y.Map or `undefined` if missing (read-only callers). */
  private notesMapOrUndefined(): Y.Map<Y.Map<unknown>> | undefined {
    const sub = this.submapOrUndefined();
    return sub?.get(DECK_SUBMAP_KEYS.notes) as Y.Map<Y.Map<unknown>> | undefined;
  }

  /** All note ids currently present on this deck. */
  listNoteIds(): string[] {
    const notes = this.notesMapOrUndefined();
    return notes ? Array.from(notes.keys()) : [];
  }

  /** The kind of a given note, or `undefined` if the note doesn't exist. */
  getNoteKind(noteId: string): NoteKind | undefined {
    const notes = this.notesMapOrUndefined();
    const item = notes?.get(noteId);
    const type = item?.get(NOTE_ITEM_KEYS.type);
    if (type === 'tiptap' || type === 'youtube' || type === 'link') {
      return type;
    }
    return undefined;
  }

  /**
   * Ensure a NoteItem Y.Map exists for `noteId` and has the requested kind.
   * If the item exists with a different kind, this DOES NOT convert it —
   * conversions need an explicit caller that knows the old/new fields to
   * preserve. For new notes, this is the only way to get a typed item.
   */
  private ensureNoteItem(noteId: string, kind: NoteKind): Y.Map<unknown> {
    const notes = this.notesMap();
    let item = notes.get(noteId);
    if (!item) {
      item = new Y.Map<unknown>();
      notes.set(noteId, item);
    }
    if (item.get(NOTE_ITEM_KEYS.type) == null) {
      item.set(NOTE_ITEM_KEYS.type, kind);
    }
    return item;
  }

  // ── Tiptap notes ────────────────────────────────────────────────────

  /**
   * Get (or create) the Y.XmlFragment that backs a tiptap note.
   *
   * If no note exists for this id, creates a fresh tiptap note (type:
   * "tiptap", content: new Y.XmlFragment). If a note exists with a
   * different kind (e.g. "youtube"), returns `null` — the caller MUST
   * check kind first via `getNoteKind()`.
   *
   * NOTE: first call auto-creates an empty fragment. `hasNoNotes()` is
   * the right probe for "is there meaningful content" — `notesMap.size`
   * alone is misleading after this method.
   */
  getNoteFragment(noteId: string = DEFAULT_NOTE_ID): Y.XmlFragment | null {
    const existingKind = this.getNoteKind(noteId);
    if (existingKind !== undefined && existingKind !== 'tiptap') {
      // Kind mismatch — don't silently create a fragment on a non-tiptap note.
      return null;
    }
    const willCreate = existingKind === undefined;
    if (willCreate) {
      // Diagnostic guard: if the doc has zero decks AND only the local
      // clientID has any ops, we're auto-creating on what looks like a
      // pristine pre-sync doc. This is the data-loss race that wiped
      // notes before — log loudly so any regression is immediately
      // visible in console.
      warnIfPreSyncAutoCreate(this.userDoc.ydoc, this.deckName, noteId);
    }
    const item = this.ensureNoteItem(noteId, 'tiptap');
    let frag = item.get(NOTE_ITEM_KEYS.content) as Y.XmlFragment | undefined;
    if (!frag) {
      frag = new Y.XmlFragment();
      item.set(NOTE_ITEM_KEYS.content, frag);
    }
    return frag;
  }

  /**
   * Side-effect-free variant of `getNoteFragment`. Returns the fragment if
   * a tiptap note already exists at `noteId`; returns `null` otherwise
   * (missing note, wrong kind, or no submap yet). Does not create anything.
   *
   * Prefer this for read-only observers (probes, snapshots, non-editor UI).
   * Use `getNoteFragment` only from the editor mount path, where auto-create
   * is the desired behaviour.
   */
  peekNoteFragment(noteId: string = DEFAULT_NOTE_ID): Y.XmlFragment | null {
    if (this.getNoteKind(noteId) !== 'tiptap') return null;
    const item = this.notesMapOrUndefined()?.get(noteId);
    const frag = item?.get(NOTE_ITEM_KEYS.content) as Y.XmlFragment | undefined;
    return frag ?? null;
  }

  /**
   * True if no note on this deck has any meaningful content.
   *
   * A tiptap note with an empty Y.XmlFragment counts as empty (consistent
   * with the old `hasNoNotes` semantics — `getNoteFragment` auto-creates
   * an empty fragment as a side-effect). Non-tiptap kinds (youtube, link)
   * count as non-empty iff they have a `url` set.
   */
  hasNoNotes(): boolean {
    const notes = this.notesMapOrUndefined();
    if (!notes || notes.size === 0) return true;
    for (const item of notes.values()) {
      const kind = item.get(NOTE_ITEM_KEYS.type);
      if (kind === 'tiptap') {
        const frag = item.get(NOTE_ITEM_KEYS.content) as Y.XmlFragment | undefined;
        if (frag && frag.length > 0) return false;
      } else if (kind === 'youtube' || kind === 'link') {
        if (item.get(NOTE_ITEM_KEYS.url)) return false;
      }
    }
    return true;
  }

  // ── YouTube notes ──────────────────────────────────────────────────

  /** Metadata stored for a youtube-kind note. */
  getYoutubeNoteMeta(noteId: string): YoutubeNoteMeta | undefined {
    if (this.getNoteKind(noteId) !== 'youtube') return undefined;
    const item = this.notesMapOrUndefined()?.get(noteId);
    if (!item) return undefined;
    const url = item.get(NOTE_ITEM_KEYS.url) as string | undefined;
    if (!url) return undefined;
    return {
      url,
      title: item.get(NOTE_ITEM_KEYS.title) as string | undefined,
      thumbnail: item.get(NOTE_ITEM_KEYS.thumbnail) as string | undefined,
      description: item.get(NOTE_ITEM_KEYS.description) as string | undefined,
    };
  }

  /** Create / update a youtube-kind note. */
  setYoutubeNoteMeta(noteId: string, meta: YoutubeNoteMeta): void {
    this.ydoc.transact(() => {
      const item = this.ensureNoteItem(noteId, 'youtube');
      item.set(NOTE_ITEM_KEYS.type, 'youtube');
      item.set(NOTE_ITEM_KEYS.url, meta.url);
      if (meta.title != null) item.set(NOTE_ITEM_KEYS.title, meta.title);
      if (meta.thumbnail != null) item.set(NOTE_ITEM_KEYS.thumbnail, meta.thumbnail);
      if (meta.description != null)
        item.set(NOTE_ITEM_KEYS.description, meta.description);
    });
  }

  // ── Link notes ─────────────────────────────────────────────────────

  /** Metadata stored for a link-kind note. */
  getLinkNoteMeta(noteId: string): LinkNoteMeta | undefined {
    if (this.getNoteKind(noteId) !== 'link') return undefined;
    const item = this.notesMapOrUndefined()?.get(noteId);
    if (!item) return undefined;
    const url = item.get(NOTE_ITEM_KEYS.url) as string | undefined;
    if (!url) return undefined;
    return {
      url,
      title: item.get(NOTE_ITEM_KEYS.title) as string | undefined,
      description: item.get(NOTE_ITEM_KEYS.description) as string | undefined,
      favicon: item.get(NOTE_ITEM_KEYS.favicon) as string | undefined,
    };
  }

  /** Create / update a link-kind note. */
  setLinkNoteMeta(noteId: string, meta: LinkNoteMeta): void {
    this.ydoc.transact(() => {
      const item = this.ensureNoteItem(noteId, 'link');
      item.set(NOTE_ITEM_KEYS.type, 'link');
      item.set(NOTE_ITEM_KEYS.url, meta.url);
      if (meta.title != null) item.set(NOTE_ITEM_KEYS.title, meta.title);
      if (meta.description != null)
        item.set(NOTE_ITEM_KEYS.description, meta.description);
      if (meta.favicon != null) item.set(NOTE_ITEM_KEYS.favicon, meta.favicon);
    });
  }

  /** Delete a note outright. Works for any kind. */
  deleteNote(noteId: string): void {
    const notes = this.notesMapOrUndefined();
    if (notes?.has(noteId)) notes.delete(noteId);
  }

  // ── Reserved sub-slices (unchanged public API) ─────────────────────

  /** Future — annotations Y.Map under this deck. */
  getAnnotationsMap(): Y.Map<unknown> {
    const sub = this.submap();
    let m = sub.get(DECK_SUBMAP_KEYS.annotations) as Y.Map<unknown> | undefined;
    if (!m) {
      m = new Y.Map();
      sub.set(DECK_SUBMAP_KEYS.annotations, m);
    }
    return m;
  }

  /** Future — per-page handwriting strokes. */
  getHandwritingArray(page: number): Y.Array<unknown> {
    const sub = this.submap();
    let hw = sub.get(DECK_SUBMAP_KEYS.handwriting) as Y.Map<Y.Array<unknown>> | undefined;
    if (!hw) {
      hw = new Y.Map<Y.Array<unknown>>();
      sub.set(DECK_SUBMAP_KEYS.handwriting, hw);
    }
    const key = `page-${page}`;
    let arr = hw.get(key);
    if (!arr) {
      arr = new Y.Array();
      hw.set(key, arr);
    }
    return arr;
  }

  /** Future — heavy embed state keyed by id (see old file for pattern). */
  getEmbed(id: string): Y.Map<unknown> {
    const sub = this.submap();
    let embeds = sub.get(DECK_SUBMAP_KEYS.embeds) as Y.Map<Y.Map<unknown>> | undefined;
    if (!embeds) {
      embeds = new Y.Map<Y.Map<unknown>>();
      sub.set(DECK_SUBMAP_KEYS.embeds, embeds);
    }
    let m = embeds.get(id);
    if (!m) {
      m = new Y.Map();
      embeds.set(id, m);
    }
    return m;
  }
}
